import {
	ProductTagsSectionInCard,
	showProductDetailsModal,
} from "@/components/common/product-listing-card";
import UpVoteButton from "@/components/common/upvote-button";
import { useProductEngagement } from "@/hooks/useProductEngagement";
import { cn } from "@/lib/utils";
import { useModal } from "@/providers/modal-provider";
import { CheckCircle2Icon, ImageIcon, MapPinIcon } from "lucide-react";
import Image from "next/image";

export type TDemoListViewCardProps = {
	productId: string;
	name: string;
	description: string;
	slug: string;
	logo?: string | null;
	features: string[] | undefined;
	hqLocation?: string | null;
	tags: {
		id: string;
		name: string;
	}[];
	isClaimed?: boolean | null;
	isPremium?: boolean;
	featuredLabel?: string;
	tagline: string;
	industriesServed: string[];
};

const DemoListViewCard = (props: TDemoListViewCardProps) => {
	const {
		productId,
		logo,
		name,
		slug,
		description,
		features,
		hqLocation,
		tags,
		isPremium,
		featuredLabel,
		tagline,
		industriesServed,
	} = props;

	const { rating, reviewsCount, commentsCount, votesData } = useProductEngagement(slug);
	const { openModal } = useModal();
	const upVotes = votesData?.data.votesCount;
	const isUpvotedByUser = votesData?.data.isUpvotedByUser;

	return (
		<div
			onClick={() => showProductDetailsModal(name, slug, openModal)}
			className={cn(
				"relative rounded-md bg-white shadow-sm first:mt-8",
				isPremium ? "bg-[url('/images/listings/sales-demo-listing-product-premium-skin.png')]" : ""
			)}
		>
			{featuredLabel ? (
				<div className="absolute -ml-8 -mt-6">
					<div className="w-max bg-gradient-to-br from-blue-500 to-brand-primary px-4 py-2 text-sm text-white">
						{featuredLabel}
					</div>

					<div className="h-0 w-0 border-b-[16px] border-r-[32px] border-b-transparent border-r-blue-600"></div>
				</div>
			) : null}

			<div className="flex h-full items-center">
				<div className={cn("w-2 self-stretch rounded-br-lg rounded-tr-lg p-1")} />

				{isPremium && (
					<div className="absolute bottom-0 h-1 w-full self-stretch rounded-b-md bg-gradient-to-r from-[#A97E2F] via-[#E0C265] to-[#C89440] p-0.5" />
				)}

				<div className="flex flex-col sm:grid sm:grid-cols-5">
					<div className="sm:col-span-4 md:border-r">
						<div className="flex items-start gap-3 px-6 py-4 max-md:flex-wrap">
							<div className="aspect-square h-20 w-20 rounded-lg border">
								{logo ? (
									<Image
										src={logo}
										alt={name}
										width={80}
										height={80}
										className="h-full w-full rounded-md object-contain"
									/>
								) : (
									<ImageIcon className="h-5 w-5 text-gray-400" />
								)}
							</div>

							<div className="text-left">
								<button
									className={cn(
										"mb-1 inline-block text-left font-bold leading-tight md:text-xl md:leading-normal",
										isPremium ? "text-white" : "text-gray-900"
									)}
								>
									{name} <span className="text-sm text-white">| {tagline}</span>
								</button>
								<p className={cn("text-left text-sm", isPremium ? "text-white" : "text-gray-500")}>
									{description}
								</p>

								<div className="mt-3 flex flex-wrap items-center justify-start gap-x-6 gap-y-2">
									{features?.map((feature) => (
										<div key={feature} className="flex items-center justify-start gap-1">
											<span>
												<CheckCircle2Icon
													className={cn(
														"fill-brand-primary",
														isPremium ? "stroke-blue-900" : "stroke-white"
													)}
												/>
											</span>
											<span className={cn("text-xs", isPremium ? "text-white" : "text-gray-500")}>
												{feature}
											</span>
										</div>
									))}
								</div>
							</div>
						</div>

						<div className="px-6 py-4">
							<ProductTagsSectionInCard
								tags={tags}
								industriesServed={industriesServed}
								commentsCount={commentsCount}
							/>
						</div>
					</div>

					<div className="flex flex-col items-center justify-center gap-3 p-4 sm:col-span-1">
						<UpVoteButton
							id={productId}
							name={name}
							slug={slug}
							upVotes={upVotes || 0}
							rating={rating || "0"}
							reviewsCount={reviewsCount || 0}
							isUpvoted={isUpvotedByUser ?? false}
							keyToInvalidate={["votes", slug]}
							isPremium={true}
						/>

						<div
							className={cn(
								"hidden items-center gap-1 rounded-lg p-2 text-sm leading-4 md:flex",
								isPremium ? "bg-white text-gray-600" : "text-gray-400"
							)}
						>
							<MapPinIcon className="h-3 w-3 text-blue-600" />
							{hqLocation}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default DemoListViewCard;
